/**
 * Runtime configuration interface and helper functions.
 *
 * Enables static SPA builds (@pureid/admin, @pureid/portal) to be parameterized
 * at container launch time via window.__PUREID_CONFIG__ (injected via /config.js)
 * rather than hardcoding domain/API URLs into JS bundles at build time.
 */

export interface PureIDRuntimeConfig {
  BASE_DOMAIN?: string;
  PUREID_API_URL?: string;
  PUREID_BACKEND_URL?: string;
}

declare global {
  interface Window {
    __PUREID_CONFIG__?: PureIDRuntimeConfig;
  }
}

/**
 * Get the effective base domain for tenant subdomains ({slug}.{baseDomain}).
 * Order of precedence:
 * 1. window.__PUREID_CONFIG__.BASE_DOMAIN (runtime container config / /config.js)
 * 2. import.meta.env.VITE_BASE_DOMAIN (compile-time fallback / dev server)
 * 3. Empty string (path-based mode fallback)
 */
export function getBaseDomain(): string {
  if (typeof window !== "undefined" && window.__PUREID_CONFIG__?.BASE_DOMAIN !== undefined) {
    return window.__PUREID_CONFIG__.BASE_DOMAIN;
  }
  return (import.meta as ImportMeta).env?.VITE_BASE_DOMAIN ?? "";
}

/**
 * Get the effective Admin API base URL.
 * Order of precedence:
 * 1. window.__PUREID_CONFIG__.PUREID_API_URL
 * 2. import.meta.env.VITE_PUREID_API_URL
 * 3. Empty string (same-origin fallback)
 */
export function getAdminApiUrl(): string {
  if (typeof window !== "undefined" && window.__PUREID_CONFIG__?.PUREID_API_URL !== undefined) {
    return window.__PUREID_CONFIG__.PUREID_API_URL;
  }
  return (import.meta as ImportMeta).env?.VITE_PUREID_API_URL ?? "";
}

/**
 * Get the effective Portal backend base URL.
 * Order of precedence:
 * 1. window.__PUREID_CONFIG__.PUREID_BACKEND_URL
 * 2. import.meta.env.VITE_PUREID_BACKEND_URL
 * 3. Empty string (same-origin / subdomain mode fallback)
 */
export function getPortalBackendUrl(): string {
  if (typeof window !== "undefined" && window.__PUREID_CONFIG__?.PUREID_BACKEND_URL !== undefined) {
    return window.__PUREID_CONFIG__.PUREID_BACKEND_URL;
  }
  return (import.meta as ImportMeta).env?.VITE_PUREID_BACKEND_URL ?? "";
}
